import React, {useState, useEffect, useCallback, useMemo} from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, User, Users, Activity, Trash2, Edit, Key, Eye, Ban, CheckCircle } from 'lucide-react';
import Button from '@shared/Button';
import Input from '@shared/Input';
import Card from '@shared/Card';
import ResponsiveTable from '@shared/ResponsiveTable';
import UserFormModal from '@/components/users/UserFormModal';
import api from '@/utils/api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import ToggleSwitch from '@shared/ToggleSwitch';
import ConfirmationModal from '@shared/ConfirmationModal';
import EmptyState from '@shared/EmptyState';
import ActionDropdown from "@shared/ActionDropdown";

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  status: 'active' | 'inactive';
  lastLogin?: string;
  createdAt: string;
  [key: string]: any;
}

interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  avgQueriesPerUser: number;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [showResetPasswordConfirmation, setShowResetPasswordConfirmation] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null);
  const [selectedUsers, setSelectedUsers] = useState(new Set<string>());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [userStatistics, setUserStatistics] = useState<UserStatistics>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    avgQueriesPerUser: 0
  });
  // State to manage which dropdown is open
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search: searchQuery,
        role: roleFilter,
        status: statusFilter,
      };
      const response = await api.getUsers(params);
      setUsers(response.users);
      setTotalPages(response.totalPages);
    } catch (error: any) {
      toast.error('Failed to load users.');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, roleFilter, statusFilter]);

  const fetchUserStats = async () => {
    const userStats: UserStatistics = {
      totalUsers: 0,
      activeUsers: 0,
      newUsersThisMonth: 0,
      avgQueriesPerUser: 0,
    };

    try {
      const totalUsersResponse = await api.getUsers({limit: 1 });
      const allUsers = await api.getUsers({ limit: totalUsersResponse.total });
      const analyticsOverview = await api.getAnalyticsOverview();

      userStats.totalUsers = totalUsersResponse.total;
      userStats.activeUsers = allUsers.users.filter((u: User) => u.status === 'active').length;

      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
      userStats.newUsersThisMonth = allUsers.users.filter((u: User) => new Date(u.createdAt) > oneMonthAgo).length;

      userStats.avgQueriesPerUser = parseFloat((analyticsOverview.totalQueries / userStats.totalUsers).toFixed(1));

      setUserStatistics(userStats);

    } catch (error: any) {
      console.error("Failed to fetch user stats:", error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUserStats();
  }, [fetchUsers]);

  const handleAddUser = () => {
    setEditingUser(null);
    setShowAddEditModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowAddEditModal(true);
  };

  const handleUserFormSubmit = async () => {
    setShowAddEditModal(false);
    await fetchUsers();
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete && selectedUsers.size === 0) return;
    setBulkActionLoading(true);
    try {
      if (userToDelete) {
        await api.deleteUser(userToDelete._id);
        toast.success('User deleted successfully.');
      } else if (selectedUsers.size > 0) {
        const results = { success: 0, failed: 0, errors: [] as Array<{userId: string, error: string}> };
        for (const userId of selectedUsers) {
          try {
            await api.deleteUser(userId);
            results.success++;
          } catch (error: any) {
            results.failed++;
            results.errors.push({ userId, error: error.message });
          }
        }
        if (results.success > 0) {
          toast.success(`${results.success} users deleted successfully.`);
        }
        if (results.failed > 0) {
          toast.error(`${results.failed} users failed to delete.`);
          console.error('Bulk delete errors:', results.errors);
        }
      }
      await fetchUsers();
      setSelectedUsers(new Set());
    } catch (error: any) {
      toast.error('Failed to delete user(s).');
      console.error('Error deleting user(s):', error);
    } finally {
      setShowDeleteConfirmation(false);
      setUserToDelete(null);
      setBulkActionLoading(false);
    }
  };

  const handleResetPassword = (user: User) => {
    setUserToResetPassword(user);
    setShowResetPasswordConfirmation(true);
  };

  const confirmResetPassword = async () => {
    if (!userToResetPassword) return;
    setBulkActionLoading(true);
    try {
      const response = await api.resetUserPassword(userToResetPassword._id);
      toast.success(`Password reset successfully. Temporary password: ${response.temporaryPassword}`);
    } catch (error: any) {
      toast.error('Failed to reset password.');
      console.error('Error resetting password:', error);
    } finally {
      setShowResetPasswordConfirmation(false);
      setUserToResetPassword(null);
      setBulkActionLoading(false);
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await api.updateUser(user._id, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully.`);
      await fetchUsers();
    } catch (error: any) {
      toast.error('Failed to update user status.');
      console.error('Error updating user status:', error);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers((prevSelected) => {
      const newSelected = new Set(prevSelected);
      if (newSelected.has(userId)) {
        newSelected.delete(userId);
      } else {
        newSelected.add(userId);
      }
      return newSelected;
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.size === users.length && users.length > 0) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map((user) => user._id)));
    }
  };

  const handleBulkAction = async (action: string, value: any) => {
    if (selectedUsers.size === 0) {
      toast.error('No users selected for bulk action.');
      return;
    }

    setBulkActionLoading(true);
    const results = { success: 0, failed: 0, errors: [] as Array<{userId: string, error: string}> };

    for (const userId of selectedUsers) {
      try {
        await api.updateUser(userId, { [action]: value });
        results.success++;
      } catch (error: any) {
        results.failed++;
        results.errors.push({ userId, error: error.message });
      }
    }

    setBulkActionLoading(false);
    if (results.success > 0) {
      toast.success(`${results.success} users updated successfully.`);
    }
    if (results.failed > 0) {
      toast.error(`${results.failed} users failed to update.`);
      console.error('Bulk action errors:', results.errors);
    }
    await fetchUsers();
    setSelectedUsers(new Set());
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800';
      case 'user':
        return 'bg-blue-100 text-blue-800';
      case 'viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleDropdown = useCallback((userId: string) => {
    setOpenDropdownId(openDropdownId === userId ? null : userId);
  }, [openDropdownId]);

  // Define columns for ResponsiveTable
  const columns = useMemo(() =>[
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          className="rounded border-gray-300 text-primary-600 shadow-sm focus:ring-primary-500"
          checked={selectedUsers.size === users.length && users.length > 0}
          onChange={handleSelectAllUsers}
        />
      ),
      render: (_: any, user: User) => (
        <input
          type="checkbox"
          className="rounded shadow-sm focus:ring-primary-500"
          checked={selectedUsers.has(user._id)}
          onChange={() => handleSelectUser(user._id)}
        />
      ),
      removeSmall: true,
    },
    {
      key: 'name',
      header: 'Name',
      render: (_: string, user: User) => (
        <div
          className="flex items-center gap-3 cursor-pointer hover:text-primary-600"
          onClick={() => navigate(`/users/${user._id}`)}
        >
          {user.name}
        </div>
      ),
    },
    { key: 'email', header: 'Email', render: (_: string, user: User) => user.email },
    {
      key: 'role',
      header: 'Role',
      render: (_: string, user: User) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeClass(user.role)}`}>
          {user.role}
        </span>
      ),
    },
    { key: 'department', header: 'Department', render: (_: string, user: User) => user.department || 'N/A' },
    {
      key: 'status',
      header: 'Status',
      render: (_: string, user: User) => (
        <ToggleSwitch
          id={`user-status-${user._id}`}
          label=""
          checked={user.status === 'active'}
          onChange={() => handleToggleUserStatus(user)}
        />
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (_: string | undefined, user: User) => (user.lastLogin ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true }) : 'Never'),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_: any, user: User) => (
        <ActionDropdown
          isOpen={openDropdownId === user._id}
          onToggle={() => toggleDropdown(user._id)}
        >
          <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/users/${user._id}`)} aria-label="View User">
            <Eye className="w-4 h-4 p-0" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleEditUser(user)} aria-label="Edit User">
            <Edit className="w-4 h-4 p-0" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user)} aria-label="Reset Password">
            <Key className="w-4 h-4 p-0" />
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDeleteUser(user)} aria-label="Delete User">
            <Trash2 className="w-4 h-4 p-0" />
          </Button>
          </div>
        </ActionDropdown>
      ),
    },
  ], [openDropdownId, toggleDropdown, selectedUsers, users.length, handleSelectAllUsers, handleSelectUser, navigate, getRoleBadgeClass, handleToggleUserStatus, handleEditUser, handleResetPassword, handleDeleteUser]);


  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Users</h1>
        <Button onClick={handleAddUser} className="flex items-center gap-2">
          <Plus className="w-5 h-5" /> Add User
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Users</p>
            <p className="text-2xl font-semibold text-gray-900">{userStatistics.totalUsers}</p>
          </div>
          <Users className="w-8 h-8 text-primary-500" />
        </Card>
        <Card className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Active Users</p>
            <p className="text-2xl font-semibold text-gray-900">{userStatistics.activeUsers}</p>
          </div>
          <CheckCircle className="w-8 h-8 text-success" />
        </Card>
        <Card className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">New Users (This Month)</p>
            <p className="text-2xl font-semibold text-gray-900">{userStatistics.newUsersThisMonth}</p>
          </div>
          <User className="w-8 h-8 text-secondary-500" />
        </Card>
        <Card className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Avg. Queries per User</p>
            <p className="text-2xl font-semibold text-gray-900">{userStatistics.avgQueriesPerUser}</p>
          </div>
          <Activity className="w-8 h-8 text-warning" />
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
          <div className="relative w-full md:w-1/3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search users by name or email..."
              className="pl-10 w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-4 w-full md:w-auto justify-end">
          {/*<div className="flex items-center px-0 md:px-3 space-x-4 md:order-first">*/}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                className="appearance-none border border-gray-300 rounded-lg pl-10 pr-8 py-2 focus:ring-2 focus:ring-primary-500 bg-white"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
                <option value="viewer">Viewer</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                className="appearance-none border border-gray-300 rounded-lg pl-10 pr-8 py-2 focus:ring-2 focus:ring-primary-500 bg-white"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No users found"
            description="Try adjusting your search or filters, or add a new user."
            action={{ label: 'Add New User', onClick: handleAddUser }}
          />
        ) : (
          <>
            {/* Replaced Table with ResponsiveTable */}
            <ResponsiveTable
              data={users}
              columns={columns}
            />

            {/* Pagination */}

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6">
                <Button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1 || loading}
                  variant="secondary"
                >
                  Previous
                </Button>
                <span className="text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <Button
                  onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages || loading}
                  variant="secondary"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Bulk Actions Bar */}
      {selectedUsers.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg p-4 border-t border-gray-200 flex items-center justify-between z-50">
          <span className="text-gray-700">{selectedUsers.size} users selected</span>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setSelectedUsers(new Set())}
              disabled={bulkActionLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setUserToDelete(null); // Clear single user delete state
                setShowDeleteConfirmation(true);
              }}
              loading={bulkActionLoading}
              disabled={bulkActionLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete Selected
            </Button>
            <Button
              variant="primary"
              onClick={() => handleBulkAction('status', 'active')}
              loading={bulkActionLoading}
              disabled={bulkActionLoading}
            >
              <CheckCircle className="w-4 h-4 mr-2" /> Activate Selected
            </Button>
            <Button
              variant="warning"
              onClick={() => handleBulkAction('status', 'inactive')}
              loading={bulkActionLoading}
              disabled={bulkActionLoading}
            >
              <Ban className="w-4 h-4 mr-2" /> Deactivate Selected
            </Button>
            {/* Add more bulk actions like change role */}
          </div>
        </div>
      )}

      <UserFormModal
        isOpen={showAddEditModal}
        onClose={() => setShowAddEditModal(false)}
        onSave={handleUserFormSubmit}
        user={editingUser}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDeleteUser}
        title={userToDelete ? `Delete User: ${userToDelete.name}?` : `Delete ${selectedUsers.size} Users?`}
        message={
          userToDelete
            ? `Are you sure you want to delete ${userToDelete.name}? This action cannot be undone.`
            : `Are you sure you want to delete ${selectedUsers.size} selected users? This action cannot be undone.`
        }
        confirmText="Delete"
        confirmButtonVariant="danger"
        isLoading={bulkActionLoading}
      />

      <ConfirmationModal
        isOpen={showResetPasswordConfirmation}
        onClose={() => setShowResetPasswordConfirmation(false)}
        onConfirm={confirmResetPassword}
        title={`Reset Password for ${userToResetPassword?.name}?`}
        message="A temporary password will be generated and displayed. Please provide it to the user securely."
        confirmText="Reset Password"
        confirmButtonVariant="warning"
        isLoading={bulkActionLoading}
      />
    </div>
  );
};

export default UsersPage;
